(() => {
  'use strict';
  if (window.SportsHubEmailContext) return;

  // Public catalog data only. No email, cart token, attributes, notes or free-text line properties.
  // This is a consent-gated current-state projection, not an additional event collector.
  const STORAGE_KEY = 'sh_email_context_v2';
  const MAX_LINES = 50;
  const MAX_BYTES = 24000;
  const controller = new AbortController();
  const aliases = new Map([['es', 'es'], ['fr', 'fr'], ['de', 'de'], ['it', 'it'], ['nb', 'nb'], ['no', 'nb'], ['nl', 'nl'], ['pt', 'pt-PT'], ['pt-pt', 'pt-PT']]);
  let provider = null, state = null, ownerKey = null, timer = null, epoch = 0;
  let request = 0, fetchAbort = null, publishing = false, dirty = false, stopped = false;
  let lastClock = 0, unsubscribe = null, meaningfulChange = false, changeCounter = 0, lastPublishedIntentKey = null;
  let restoredOnce = false, consentWasAllowed = false;
  const detail = (name, value) => { const script = document.getElementById('sh-email-context'); if (script) script.dataset[name] = value; };
  const diagnostic = (value) => { const script = document.getElementById('sh-email-context'); if (script) script.dataset.state = value; };
  detail('resetReason', 'none');
  const locale = () => aliases.get(String(window.Shopify?.locale || document.documentElement.lang || '').replace(/_/g, '-').toLowerCase()) || null;
  const environment = () => document.getElementById('sh-email-context')?.dataset.qa === 'false' ? 'production' : 'preview';
  const allowed = () => {
    try {
      const p = window.Shopify?.customerPrivacy;
      return !stopped && window.Shopify?.designMode !== true && Boolean(locale()) && p?.analyticsProcessingAllowed?.() === true && p?.marketingAllowed?.() === true;
    } catch { return false; }
  };
  const id = (value) => {
    if (typeof value === 'number' && !Number.isSafeInteger(value)) return null;
    return String(value || '').match(/^(?:gid:\/\/shopify\/(?:Product|ProductVariant)\/)?([1-9]\d{0,19})$/)?.[1] || null;
  };
  const text = (value, max = 200) => typeof value === 'string' && value.length <= max && !/[<>\u0000-\u001f\u007f]/.test(value) ? value.trim() : null;
  const iso = (value) => typeof value === 'string' && /^\d{4}-\d\d-\d\dT\d\d:\d\d:\d\d\.\d{3}Z$/.test(value) && Number.isFinite(Date.parse(value)) ? value : null;
  const now = () => new Date(lastClock = Math.max(Date.now(), lastClock + 1)).toISOString();
  const uuid = () => { try { return window.crypto.randomUUID(); } catch { return null; } };
  const productLink = (value) => {
    try {
      const u = new URL(value, window.location.origin);
      const handle = u.pathname.match(/^\/(?:[a-z]{2}(?:-[A-Za-z]{2})?\/)?products\/([a-z0-9-]+)\/?$/)?.[1];
      if (u.protocol !== 'https:' || u.username || u.password || !handle) return null;
      return { handle, url: u.origin + u.pathname };
    } catch { return null; }
  };
  const market = () => {
    try {
      const raw = JSON.parse(document.getElementById('sh-email-market')?.textContent || 'null');
      const country = typeof raw?.country === 'string' && /^[A-Z]{2}$/.test(raw.country) ? raw.country : null;
      const currency = typeof raw?.currency === 'string' && /^[A-Z]{3}$/.test(raw.currency) ? raw.currency : null;
      const canonical = document.querySelector('link[rel="canonical"]')?.href;
      const candidates = [canonical, raw?.origin, window.location.origin];
      const origins = new Set(['https://sportshub.es','https://sportshub.fr','https://de.sportshubeurope.com','https://it.sportshubeurope.com','https://no.sportshubeurope.com','https://nl.sportshubeurope.com','https://pt.sportshubeurope.com','https://be.sportshubeurope.com']);
      const origin = candidates.map(value => { try { return new URL(value).origin; } catch { return null; } }).find(value => origins.has(value));
      const routeValue = [window.Shopify?.routes?.root,raw?.route_root].find(value => typeof value === 'string' && /^\/(?:[a-z]{2}(?:-[A-Za-z]{2})?\/?)?$/.test(value));
      const routeRoot = routeValue ? routeValue.endsWith('/') ? routeValue : routeValue+'/' : null;
      return country && currency && origin && routeRoot ? { locale: locale(), country, currency, origin, route_root:routeRoot } : null;
    } catch { return null; }
  };
  const minor = value => Number.isSafeInteger(value) && value > 0 && value <= 1000000000 ? value : null;
  const publicImage = value => {
    try { const url = new URL(value, window.location.origin); return url.protocol === 'https:' && !url.username && !url.password && url.hostname === 'cdn.shopify.com' ? url.origin + url.pathname : null; }
    catch { return null; }
  };
  const product = (raw, viewedAt) => {
    const link = productLink(raw?.URL || raw?.url);
    const handle = typeof (raw?.Handle || raw?.handle) === 'string' && /^[a-z0-9-]+$/.test(raw.Handle || raw.handle) ? raw.Handle || raw.handle : null;
    const productId = id(raw?.ProductID || raw?.id);
    const variantId = id(raw?.VariantID || raw?.variant_id);
    const title = text(raw?.ProductName || raw?.title);
    if (!link || !handle || !productId || !variantId || !title || !iso(viewedAt)) return null;
    let image = null;
    try {
      const u = new URL(raw.ImageURL || raw.image);
      if (u.protocol === 'https:' && !u.username && !u.password && (u.hostname === 'cdn.shopify.com' || u.origin === window.location.origin)) image = u.origin + u.pathname;
    } catch { /* An unavailable image never supplies an external fallback. */ }
    const activeMarket = market();
    if (!activeMarket || new URL(link.url).origin !== activeMarket.origin) return null;
    const variantTitle = text(raw?.VariantTitle ?? raw?.variant_title ?? '') ?? '';
    const priceMinor = minor(raw?.PriceMinor ?? raw?.price_minor);
    const currency = raw?.Currency || raw?.currency;
    return { id: productId, variant_id: variantId, ...link, handle, title, variant_title: variantTitle === 'Default Title' ? '' : variantTitle,
      price_minor: currency === activeMarket.currency ? priceMinor : null, currency: activeMarket.currency,
      ...(image ? { image } : {}), viewed_at: viewedAt };
  };
  const unknownCart = (previous) => ({ status: 'unknown', items: [], url: null, item_count: null, currency: null, observed_at: null, episode_key: null,
    changed_at: iso(previous?.changed_at), activity_at: iso(previous?.activity_at) });
  const empty = () => ({ v: 2, environment: environment(), revision: uuid(), observed_at: now(), intent_updated_at: null, locale: locale(), market: market(), last_product: null, last_family: null, cart: unknownCart() });
  const fingerprint = (cart) => cart?.status === 'known' ? JSON.stringify([cart.currency, cart.items.map(({ variant_id, quantity }) => [variant_id, quantity])]) : null;
  const cartUrl = (items) => {
    if (!items.length) return null;
    let origin = window.location.origin;
    try {
      const canonical = document.querySelector('link[rel="canonical"]')?.href;
      const u = canonical ? new URL(canonical) : null;
      if (u?.protocol === 'https:' && !u.username && !u.password && !/\.shopifypreview\.com$/.test(u.hostname)) origin = u.origin;
    } catch { /* A preview domain cannot become a customer recovery URL. */ }
    if (/\.shopifypreview\.com$/.test(new URL(origin).hostname)) return null;
    const root = /^\/(?:[a-z]{2}(?:-[A-Za-z]{2})?\/)?$/.test(window.Shopify?.routes?.root || '/') ? window.Shopify?.routes?.root || '/' : '/';
    return origin + root + 'cart/' + items.map(({ variant_id, quantity }) => variant_id + ':' + quantity).join(',');
  };
  const normalizeCart = (raw) => {
    if (!raw || !Array.isArray(raw.items) || raw.items.length > MAX_LINES || !Number.isSafeInteger(raw.item_count) || raw.item_count < 0 || !/^[A-Z]{3}$/.test(raw.currency || '') || raw.errors || raw.status >= 400) return null;
    const activeMarket = market();
    if (!activeMarket || raw.currency !== activeMarket.currency) return null;
    const items = new Map();
    let count = 0;
    for (const line of raw.items) {
      const variantId = id(line?.variant_id || line?.id), productId = id(line?.product_id);
      const quantity = line?.quantity;
      if (!variantId || !productId || !Number.isSafeInteger(quantity) || quantity < 0 || quantity > 9999) return null;
      count += quantity;
      const gift = line.properties?._sh_reward === 'gift-v1';
      // A permalink cannot reproduce arbitrary line properties or selling plans. Fail closed.
      if (line.selling_plan_allocation || Object.keys(line.properties || {}).some((key) => key !== '_sh_reward' || !gift)) return null;
      if (!quantity || gift) continue;
      const handle = typeof line.handle === 'string' && /^[a-z0-9-]+$/.test(line.handle) ? line.handle : null;
      if (!handle) return null;
      const previous = items.get(variantId);
      if (previous && (previous.product_id !== productId || previous.handle !== handle)) return null;
      const combined = (previous?.quantity || 0) + quantity;
      if (combined > 9999) return null;
      const title = text(line.product_title || line.title);
      const variantTitle = text(line.variant_title || '') ?? '';
      let lineUrl = new URL(line.url, activeMarket.origin);
      if (activeMarket.route_root !== '/' && /^\/products\//.test(lineUrl.pathname)) lineUrl = new URL(activeMarket.route_root.slice(0,-1)+lineUrl.pathname,lineUrl.origin);
      const link = productLink(lineUrl.href);
      if (!title || !link || new URL(link.url).origin !== activeMarket.origin) return null;
      const priceMinor = minor(line.final_price ?? line.price);
      const image = publicImage(line.featured_image?.url || line.image);
      items.set(variantId, { variant_id: variantId, product_id: productId, handle, quantity: combined, title,
        variant_title: variantTitle === 'Default Title' ? '' : variantTitle, url: link.url,
        price_minor: priceMinor, currency: raw.currency, ...(image ? { image } : {}) });
    }
    if (count !== raw.item_count) return null;
    const lines = [...items.values()].sort((a, b) => a.variant_id.localeCompare(b.variant_id));
    return { status: 'known', items: lines, url: cartUrl(lines), item_count: lines.reduce((n, item) => n + item.quantity, 0), currency: raw.currency };
  };
  const persist = () => {
    if (!allowed() || !state) return;
    try { window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ owner_key: ownerKey, published_intent_key: lastPublishedIntentKey, context: state })); } catch { /* Storage unavailable: current page remains usable. */ }
  };
  const clearStorage = () => { try { window.sessionStorage.removeItem(STORAGE_KEY); } catch { /* No storage is required. */ } };
  const restore = () => {
    state = empty();
    if (!allowed()) return;
    let savedText;
    try { savedText = window.sessionStorage.getItem(STORAGE_KEY); }
    catch { detail('restore', 'storage-unavailable'); return; }
    if (!savedText) { detail('restore', 'absent'); return; }
    if (savedText.length > MAX_BYTES) { detail('restore', 'rejected'); return; }
    try {
      const saved = JSON.parse(savedText), c = saved.context;
      if (c?.v !== 2 || c.environment !== environment() || !iso(c.observed_at) || Date.now() - Date.parse(c.observed_at) > 86400000 || Date.parse(c.observed_at) - Date.now() > 60000) { detail('restore', 'rejected'); return; }
      ownerKey = typeof saved.owner_key === 'string' && saved.owner_key.length <= 128 ? saved.owner_key : null;
      lastPublishedIntentKey = typeof saved.published_intent_key === 'string' && /^sh1:[a-f0-9]{64}$/.test(saved.published_intent_key) ? saved.published_intent_key : null;
      state.last_product = c.last_product ? product(c.last_product, c.last_product.viewed_at) : null;
      state.intent_updated_at = iso(c.intent_updated_at);
      state.last_family = family(c.last_family, c.last_family?.occurred_at);
      // Preserve only historical timing. Current lines must be freshly read on every page.
      state.cart.changed_at = iso(c.cart?.changed_at);
      state.cart.activity_at = iso(c.cart?.activity_at);
      state.cart.episode_key = c.cart?.status === 'known' && c.cart.item_count > 0 && /^[a-zA-Z0-9-]{8,64}$/.test(c.cart.episode_key || '') ? c.cart.episode_key : null;
      state.previous_cart_fingerprint = typeof c.previous_cart_fingerprint === 'string' && c.previous_cart_fingerprint.length < 4000 ? c.previous_cart_fingerprint : null;
      lastClock = Date.parse(c.observed_at);
      detail('restore', 'restored');
    } catch { detail('restore', 'rejected'); }
  };
  const ensureState = () => {
    if (!state) {
      if (!restoredOnce) { restore(); restoredOnce = true; }
      else state = empty();
    }
    consentWasAllowed = true;
  };
  // Stable collection IDs classify taxonomy; no product catalog, pricing or stock is hardcoded.
  const collectionFamilies = new Map([['657108894033','chalecos'],['657231577425','hidratacion'],['679412728145','mochilas_cinturones'],['694342320465','portabastones'],['694342517073','secado'],['694347530577','mochilas_cinturones'],['694347563345','hidratacion'],['694347596113','hidratacion']]);
  const searchFamily = query => {
    const q = query.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
    const families = [
      ['chalecos', /\b(?:chaleco|chalecos|vest|vests|gilet|gilets|trinkweste|trinkwesten|drikkevest|colete|skin)\b/],
      ['hidratacion', /\b(?:flask|flasks|flasque|flasques|softflask|softflasks|bidon|hidratacion|hydration|drikkeflaske|vaso|gobelet|bolsa de agua|poche a eau)\b/],
      ['portabastones', /\b(?:quiver|carcaj|carquois|portabastones|portabastoncini|porta-bastoes|stockhalter|stavholder)\b/],
      ['secado', /\b(?:secado|sechage|drying|droogstandaard|trockenstander|torkestativ|asciugatura|secagem)\b/],
      ['mochilas_cinturones', /\b(?:mochila|mochilas|backpack|rucksack|sac a dos|cinturon|cinturones|belt|belts|ceinture|gurtel|cinto|cintura|lopsebelt)\b/]
    ].filter(([,pattern]) => pattern.test(q));
    return families.length === 1 ? families[0][0] : 'busqueda_sin_resultados_o_ambigua';
  };
  const family = (raw, occurredAt) => {
    if (!raw || !iso(occurredAt)) return null;
    const source = raw.source || (raw.CollectionID ? 'collection' : raw.SearchQuery ? 'search' : null);
    const activeMarket = market();
    if (!activeMarket) return null;
    if (source === 'collection') {
      const collectionId = id(raw.collection_id || raw.CollectionID);
      const familyId = collectionFamilies.get(collectionId);
      return familyId ? { source, family: familyId, collection_id: collectionId, occurred_at: occurredAt } : null;
    }
    if (source !== 'search') return null;
    const query = text(raw.query || raw.SearchQuery,120);
    if (!query || /[@<>\u0000-\u001f\u007f]|(?:https?:\/\/|www\.)/i.test(query) || /\d[\d\s().-]{5,}\d/.test(query)) return null;
    const results = Number.isSafeInteger(raw.results_count ?? raw.ResultsCount) ? raw.results_count ?? raw.ResultsCount : null;
    if (results === null || results < 0) return null;
    return { source, family: results === 0 ? 'busqueda_sin_resultados_o_ambigua' : searchFamily(query), query, results_count: results, occurred_at: occurredAt };
  };
  const props = () => ({
    sh_email_context: JSON.stringify(state),
    sh_context_environment: state.environment,
    sh_context_revision: state.revision,
    sh_intent_updated_at: state.intent_updated_at,
    ...(state.last_product ? { sh_last_product_handle: state.last_product.handle, sh_last_product_id: state.last_product.id, sh_last_product_variant_id: state.last_product.variant_id, sh_last_product_viewed_at: state.last_product.viewed_at } : {}),
    sh_cart_url: state.cart.url || '', sh_cart_items: JSON.stringify(state.cart.items), sh_cart_status: state.cart.status,
    sh_cart_item_count: state.cart.item_count, sh_cart_activity_at: state.cart.activity_at,
    ...(state.last_family ? { sh_last_family: state.last_family.family, sh_last_family_source: state.last_family.source, sh_last_family_at: state.last_family.occurred_at } : {}),
  });
  const intentFacts = (context) => {
    const cart = context.cart, viewed = context.last_product;
    if (cart.status !== 'known') return { kind: 'unknown', signal_at: null, signature: ['unknown'] };
    if (cart.item_count > 0) return cart.activity_at ? { kind: 'cart', signal_at: cart.activity_at, signature: ['cart', cart.activity_at, cart.items.map((item) => [item.variant_id, item.quantity])] } : { kind: 'unknown', signal_at: null, signature: ['unknown'] };
    if (viewed && (!cart.activity_at || viewed.viewed_at > cart.activity_at)) return { kind: 'product', signal_at: viewed.viewed_at, signature: ['product', viewed.id, viewed.viewed_at] };
    const currentFamily = context.last_family;
    if (currentFamily && (!cart.activity_at || currentFamily.occurred_at > cart.activity_at)) return { kind: 'family', signal_at: currentFamily.occurred_at, signature: ['family',currentFamily.source,currentFamily.family,currentFamily.occurred_at,currentFamily.collection_id || currentFamily.query] };
    return { kind: 'empty', signal_at: cart.activity_at, signature: ['empty', cart.activity_at] };
  };
  const intentDescriptor = async (context) => {
    const facts = intentFacts(context);
    const bytes = new TextEncoder().encode(JSON.stringify(facts.signature));
    const digest = await window.crypto.subtle.digest('SHA-256', bytes);
    const key = 'sh1:' + Array.from(new Uint8Array(digest), (part) => part.toString(16).padStart(2, '0')).join('');
    // A full-read failure is a suppression guard, not evidence of a new shopping intent.
    return { intent_key: key, kind: facts.kind, signal_at: facts.signal_at, intentChanged: facts.kind !== 'unknown' && key !== lastPublishedIntentKey,
      revision: context.revision, environment: context.environment, observed_at: context.observed_at, meaningful: meaningfulChange };
  };
  const flush = async () => {
    clearTimeout(timer); timer = null;
    if (publishing || !dirty || !provider || !allowed() || document.hidden) return;
    publishing = true;
    const generation = epoch, currentProvider = provider;
    try {
      while (dirty && generation === epoch && currentProvider === provider && allowed() && !document.hidden) {
        dirty = false;
        const properties = props();
        const snapshot = JSON.parse(properties.sh_email_context), atCounter = changeCounter;
        if (properties.sh_email_context.length > MAX_BYTES || !state.revision) { state.cart = unknownCart(state.cart); break; }
        try {
          const descriptor = await intentDescriptor(snapshot);
          if (generation !== epoch || currentProvider !== provider || !allowed()) break;
          if (state.revision !== snapshot.revision) { dirty = true; continue; }
          for (const key of ['intent_key', 'kind', 'signal_at']) {
            snapshot[key] = descriptor[key];
            state[key] = descriptor[key];
          }
          properties.sh_email_context = JSON.stringify(snapshot);
          properties.sh_context_intent_key = descriptor.intent_key;
          const accepted = await currentProvider.publish(properties, descriptor);
          if (generation === epoch) diagnostic(accepted === false ? 'not-published' : 'published');
          if (accepted === false) break;
          if (generation === epoch) {
            if (descriptor.kind !== 'unknown') lastPublishedIntentKey = descriptor.intent_key;
            if (changeCounter === atCounter) meaningfulChange = false;
            persist();
          }
        } catch { break; }
      }
    } finally {
      publishing = false;
      if (dirty && allowed() && !timer) timer = setTimeout(flush, 80);
    }
  };
  const changed = (kind = 'observation', meaningful = false) => {
    if (!allowed()) return;
    state.observed_at = now(); state.revision = uuid(); state.locale = locale();
    meaningfulChange ||= meaningful;
    if (meaningful) changeCounter++;
    if (meaningful) state.intent_updated_at = state.observed_at;
    diagnostic('pending');
    persist(); dirty = true;
    if (!timer) timer = setTimeout(flush, 80);
  };
  const invalidate = (clear = true, reason = 'reset') => {
    epoch++; request++; fetchAbort?.abort(); fetchAbort = null;
    clearTimeout(timer); timer = null; dirty = false;
    state = null; ownerKey = null;
    restoredOnce = true;
    meaningfulChange = false; changeCounter++; lastPublishedIntentKey = null;
    diagnostic('blocked');
    detail('resetReason', reason);
    if (clear) clearStorage();
  };
  const observeCart = (raw, options = {}) => {
    if (!allowed() || document.hidden) return false;
    ensureState();
    const normalized = normalizeCart(raw);
    // Theme pubsub can provide a partial fragment. Only its following full GET is authoritative.
    if (!normalized && options.partial === true) return false;
    request++; fetchAbort?.abort(); fetchAbort = null;
    const at = now(), previous = state.cart;
    if (!normalized) { state.cart = unknownCart(state.cart); changed('unknown'); return false; }
    const before = fingerprint(previous) || state.previous_cart_fingerprint;
    detail('prevFingerprint', String(Boolean(before)));
    detail('previousActivity', String(Boolean(previous.activity_at)));
    const different = before !== fingerprint(normalized);
    const firstEmpty = !before && normalized.item_count === 0;
    const observedSelectionChange = !firstEmpty && different;
    const initialPositive = normalized.item_count > 0 && !previous.activity_at;
    const actualActivity = options.activity === true || observedSelectionChange || initialPositive;
    state.cart = { ...normalized, observed_at: at, episode_key: normalized.item_count > 0 ? previous.episode_key || uuid() : null,
      changed_at: observedSelectionChange ? at : previous.changed_at,
      activity_at: actualActivity ? at : previous.activity_at };
    state.previous_cart_fingerprint = fingerprint(normalized);
    const meaningful = actualActivity;
    changed(meaningful ? normalized.item_count ? 'cart' : 'empty' : 'observation', meaningful); return true;
  };
  const refreshCart = async (options = {}) => {
    if (!allowed() || document.hidden) return false;
    ensureState();
    const generation = epoch, ticket = ++request;
    fetchAbort?.abort(); const abort = new AbortController(); fetchAbort = abort;
    const root = /^\/(?:[a-z]{2}(?:-[A-Za-z]{2})?\/)?$/.test(window.Shopify?.routes?.root || '/') ? window.Shopify?.routes?.root || '/' : '/';
    try {
      const response = await window.fetch(root + 'cart.js', { method: 'GET', credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' }, signal: abort.signal });
      if (!response.ok || generation !== epoch || ticket !== request || !allowed() || document.hidden) throw new Error('Current cart unavailable');
      const raw = await response.json();
      if (generation !== epoch || ticket !== request || !allowed() || document.hidden) return false;
      fetchAbort = null;
      return observeCart(raw, { ...options, partial: false });
    } catch {
      if (generation === epoch && ticket === request && allowed()) { fetchAbort = null; state.cart = unknownCart(state.cart); changed('unknown'); }
      return false;
    }
  };
  const observeProduct = (raw) => {
    if (!allowed() || document.hidden) return false;
    if (!Number.isFinite(Number(raw?.Price)) || Number(raw.Price) <= 0) return false;
    ensureState();
    const next = product(raw, now());
    if (!next) return false;
    // The caller must invoke this only after the SDK accepted an identified Viewed Product.
    state.last_product = next; changed('product', true); return true;
  };
  const observeVariant = (raw) => {
    if (!allowed() || document.hidden || !state?.last_product || state.last_product.id !== id(raw?.product_id) || !id(raw?.variant_id)) return false;
    state.last_product.variant_id = id(raw.variant_id);
    const selected = Array.isArray(raw.selected_options) ? raw.selected_options.map(option => text(option?.value)).filter(Boolean) : [];
    state.last_product.variant_title = selected.length ? selected.join(' / ') : '';
    state.last_product.price_minor = Number.isFinite(raw.price) && raw.currency === state.market?.currency ? minor(Math.round(raw.price * 100)) : null;
    // A variant change is a fresh product signal, with the actual selected variant.
    state.last_product.viewed_at = now();
    changed('product', true); return true;
  };
  const observeFamily = raw => {
    if (!allowed() || document.hidden) return false;
    ensureState();
    const next = family(raw, now());
    if (!next) return false;
    state.last_family = next; changed('family', true); return true;
  };
  const resetIdentity = () => { invalidate(true, 'identity'); if (allowed()) { state = empty(); refreshCart(); } };
  const useIdentity = (key) => {
    if (!allowed() || typeof key !== 'string' || !/^[a-zA-Z0-9:_-]{1,128}$/.test(key)) return false;
    ensureState();
    if (ownerKey !== key) resetIdentity();
    ownerKey = key; persist(); return true;
  };
  window.SportsHubEmailContext = Object.freeze({
    bindProvider(next) {
      provider = typeof next?.publish === 'function' ? next : null;
      if (provider && allowed()) { ensureState(); if (dirty) flush(); refreshCart(); }
    },
    observeProduct, observeVariant, observeFamily, observeCart, refreshCart, resetIdentity, useIdentity,
    destroy() { stopped = true; invalidate(true, 'destroy'); provider = null; controller.abort(); unsubscribe?.(); },
  });
  const start = () => {
    if (!allowed()) return;
    ensureState(); refreshCart();
  };
  document.addEventListener('visitorConsentCollected', () => {
    if (!allowed()) { consentWasAllowed = false; invalidate(true, 'consent-revoked'); return; }
    // Shopify can announce already-accepted preferences again on each navigation.
    // An allowed→allowed notification must not erase the restored cart episode.
    detail('consentTransition', consentWasAllowed ? 'allowed-unchanged' : 'allowed');
    ensureState(); refreshCart();
  }, { signal: controller.signal });
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) { request++; fetchAbort?.abort(); fetchAbort = null; }
    else if (allowed()) { flush(); refreshCart(); }
  }, { signal: controller.signal });
  window.addEventListener('pagehide', () => { epoch++; request++; fetchAbort?.abort(); clearTimeout(timer); timer = null; dirty = false; }, { signal: controller.signal });
  window.addEventListener('pageshow', (event) => { if (event.persisted && allowed()) { ensureState(); refreshCart(); } }, { signal: controller.signal });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true, signal: controller.signal });
  else start();
})();
